import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppBar } from "../../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../../components/desktopUi";
import { DetailTabBar } from "../../../components/financeDetail";
import { CenterSpinner, EmptyText, M, PropCell } from "../../../components/managementUi";
import {
  KpiConsultationTab,
  KpiEvaluationTab,
  KpiLeadTab,
  KpiTransactionTab,
} from "../../../components/kpiDetailTabs";
import { useEmployeeAuth } from "../../../auth/EmployeeAuthContext";
import { isSupportDepartment } from "../../../utils/managerRole";
import { getMyKpiDetailApi } from "../../../api/evaluation.api";
import { EMP_BASE_PATH } from "../../../config";

const TABS = [
  { key: "overview", title: "Tổng quan" },
  { key: "consultation", title: "Tư vấn" },
  { key: "lead", title: "Leads" },
  { key: "transaction", title: "Giao dịch" },
  { key: "evaluation", title: "Đánh giá" },
];

// Khớp StaffEmployeeOverviewTab của app (statistical-report/detail.tsx).
const OverviewTab = ({ data, onOpenEmployee, onOpenAttendance }) => {
  const statusLabel = useMemo(() => {
    const cls = data?.evaluation?.classification;
    if (cls?.code === "DAT" && Number(data?.evaluation?.metrics?.consecutiveMonthsWithoutTransactions || 0) >= 2) {
      return `${cls?.label || "-"} (*)`;
    }
    return cls?.label || "-";
  }, [data]);

  return (
    <Box>
      <PropCell label="Đánh giá" value={statusLabel} />
      <PropCell label="Tháng" value={`${data?.period?.month || "-"}/${data?.period?.year || "-"}`} />
      <PropCell label="Tên nhân viên" value={data?.employee?.fullName || "-"} onPress={onOpenEmployee} />
      <PropCell label="Mã nhân viên" value={data?.employee?.code || "-"} />
      <PropCell label="Email" value={data?.employee?.email || "-"} />
      <PropCell label="Số điện thoại" value={data?.employee?.phone || "-"} />
      <PropCell label="Chi nhánh" value={data?.employee?.branch?.name || "-"} />
      <PropCell label="Phòng" value={data?.employee?.department?.name || "-"} />
      <PropCell label="Khối" value={data?.employee?.block?.name || "-"} />
      <PropCell label="Vùng" value={data?.employee?.area?.name || "-"} />
      <PropCell label="Báo cáo công" value="Xem chi tiết" onPress={onOpenAttendance} />
      <PropCell label="Ghi chú" value={data?.evaluation?.note || "-"} />
    </Box>
  );
};

const EmployeeKpiDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { code } = useParams();
  const [search] = useSearchParams();
  const { user } = useEmployeeAuth();
  const month = Number(search.get("month")) || new Date().getMonth() + 1;
  const year = Number(search.get("year")) || new Date().getFullYear();

  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    getMyKpiDetailApi({ month, year, code })
      .then((d) => setDetail(d || null))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [code, month, year]);

  const activeKey = TABS[tab]?.key;
  const isEvaluationTab = activeKey === "evaluation";
  const canAddEvaluation = !isSupportDepartment(user?.supportDepartment?.code);

  const renderTab = () => {
    if (loading) return <CenterSpinner />;
    if (!detail) return <EmptyText text="Không tìm thấy thông tin" />;

    if (activeKey === "overview") {
      return (
        <OverviewTab
          data={detail}
          onOpenEmployee={() =>
            navigate(`${EMP_BASE_PATH}/quan-ly/nhan-vien/${detail?.employee?.code}/thong-tin`)
          }
          onOpenAttendance={() =>
            navigate(
              `${EMP_BASE_PATH}/quan-ly/chuyen-can/${detail?.employee?.id || detail?.employee?._id}` +
                `?month=${detail?.period?.month || month}&year=${detail?.period?.year || year}` +
                `&name=${encodeURIComponent(detail?.employee?.fullName || "")}` +
                `&code=${encodeURIComponent(detail?.employee?.code || "")}`
            )
          }
        />
      );
    }
    if (activeKey === "consultation") return <KpiConsultationTab data={detail} />;
    if (activeKey === "lead") return <KpiLeadTab data={detail} />;
    if (activeKey === "transaction") return <KpiTransactionTab data={detail} />;
    return <KpiEvaluationTab data={detail} />;
  };

  const pageBody = (
    <>
      <DetailTabBar tabs={TABS} index={tab} onChange={setTab} />
      <Box px="16px" pb={isEvaluationTab && canAddEvaluation ? "80px" : "0"}>
        {renderTab()}
      </Box>

      {/* Nút "Thêm đánh giá" cố định đáy - chỉ hiện ở tab Đánh giá (khớp app). */}
      {!loading && detail && isEvaluationTab && canAddEvaluation && (
        <Box
          position={{ base: "fixed", md: "static" }}
          bottom={{ base: "0", md: "auto" }}
          left={{ base: "50%", md: "auto" }}
          transform={{ base: "translateX(-50%)", md: "none" }}
          w="100%"
          maxW={{ base: "480px", md: "100%" }}
          bg="white"
          borderTop="1px solid"
          borderColor={M.border}
          px="16px"
          pt="12px"
          pb="12px"
          zIndex={20}
        >
          <Flex
            as="button"
            type="button"
            w="100%"
            h="48px"
            align="center"
            justify="center"
            borderRadius="8px"
            bg={M.primary}
            onClick={() =>
              navigate(
                `${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-nhan-vien/${code}/them-danh-gia?month=${month}&year=${year}`
              )
            }
          >
            <Text fontSize="16px" fontWeight={700} color="white">
              + Thêm đánh giá
            </Text>
          </Flex>
        </Box>
      )}
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết KPI" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Chi tiết KPI" />
      {pageBody}
    </Box>
  );
};

export default EmployeeKpiDetail;
